/**
 * **Sitrep zone-geometry primitives** (Core pp. 267–273) — the pure occupancy/control
 * reads every zone-scoring victory predicate and the unit-AI eval terms compose. Two
 * functions:
 *
 *   - {@link unitsInZone} — every combatant (any kind, character or object) whose
 *     footprint intersects a {@link SitrepZone}'s rects.
 *   - {@link controlOf} — the {@link Side} that *controls* a zone (RAW: only one
 *     side's characters present), `"contested"` (2+ sides present), or `null` (none).
 *
 * Composes the exact pattern `packages/ai/src/evaluator.ts`'s `positionTerm` already
 * uses — `worldFor(state).footprint(pos, size).cells()` (here, the shared
 * `footprintCells` helper wrapping that same call) intersected against zone geometry —
 * with `sideOf` for allegiance and `rectContainsCell` for cell∈zone (the {@link
 * RoomRect} vocabulary {@link SitrepZone.rects} reuses).
 *
 * **Zone geometry is a `2d-square`-cell concept** (mirroring rooms, which share the
 * same `RoomRect` shape for the same reason — `RoomRect.x`/`.y` are square-grid
 * columns/rows). A footprint cell that isn't square-shaped (a hex/3d-hex encounter's
 * `HexCell`) can never satisfy `rectContainsCell` — {@link squareXY} returns `null`
 * for it — so both primitives quietly read "not in any zone" for a hex-mode encounter
 * rather than throwing; sitrep authoring on a hex encounter is a GM/UI-level footgun,
 * not a reason to crash a pure query.
 *
 * **Only characters count toward control** ({@link controlOf}) — an inert Escort/
 * Extraction Objective (`kind: "object"`) sitting in a Control Zone does not contest
 * it, matching RAW's "characters" framing (Core p.268) and this codebase's
 * character/object split (`isCharacter`). {@link unitsInZone} itself stays kind-agnostic
 * (an Escort objective needs "is the objective in the extraction zone," which is squarely
 * an object) — the character filter lives in `controlOf`, not the shared occupancy
 * primitive.
 *
 * **No liveness (HP) filter.** Neither primitive checks `current.hp > 0` — a combatant
 * occupies/controls a zone by position alone. RAW never addresses "does a wrecked mech
 * still contest a CZ," so it is left to the per-sitrep predicates; flagged here as an
 * explicit engine-level default rather than a silent assumption.
 */

import {
  isCharacter,
  rectContainsCell,
  sideOf,
  worldFor,
  type Cell,
  type Combatant,
  type EncounterState,
  type Side,
  type SitrepZone,
} from "@lancer-vtt/domain";
import { footprintCells } from "../effects/spatialAnswerer";

/** Narrow a {@link Cell} to its square `{x,y}` fields, or `null` if it's a hex cell. */
function squareXY(cell: Cell): { x: number; y: number } | null {
  return "x" in cell && "y" in cell ? { x: cell.x, y: cell.y } : null;
}

/** Whether any footprint cell falls inside any of `zone`'s rects. */
function footprintInZone(cells: Cell[], zone: SitrepZone): boolean {
  return cells.some((cell) => {
    const xy = squareXY(cell);
    return xy !== null && zone.rects.some((r) => rectContainsCell(r, xy.x, xy.y));
  });
}

/**
 * Every combatant (placed, any kind) whose footprint intersects `zone`'s rects. Kind-
 * agnostic — an Escort/Extraction Objective (`kind: "object"`) is as eligible as a
 * mech; callers that need "characters only" (e.g. {@link controlOf}) filter with
 * {@link isCharacter} themselves. Unplaced combatants (`pos == null`) never match.
 */
export function unitsInZone(state: EncounterState, zone: SitrepZone): Combatant[] {
  const world = worldFor(state);
  return Object.values(state.combatants).filter((c) => {
    if (c.pos == null) return false;
    return footprintInZone(footprintCells(world, c), zone);
  });
}

/**
 * The {@link Side} that controls `zone` — RAW's Control Zone rule (Core p.268):
 * `null` if no side's characters occupy it, the lone {@link Side} if only one side's
 * characters do, `"contested"` if 2+ sides' characters both do. Only {@link
 * isCharacter} combatants count (objects never contest a CZ); filters {@link
 * unitsInZone}'s kind-agnostic result rather than re-deriving occupancy.
 */
export function controlOf(state: EncounterState, zone: SitrepZone): Side | "contested" | null {
  const sides = new Set<Side>();
  for (const c of unitsInZone(state, zone)) {
    if (!isCharacter(c)) continue;
    sides.add(sideOf(c));
  }
  if (sides.size === 0) return null;
  if (sides.size > 1) return "contested";
  // `sides.size === 1` guarantees exactly one iteration; the loop is just how TS narrows
  // a `Set`'s sole element without `noUncheckedIndexedAccess` flagging an array index.
  for (const only of sides) return only;
  return null;
}
